import { z } from 'zod';
import { apiDate } from '@/common/api/schemas';

// ─── Telemetry field catalog ───────────────────────────────────────────────────

export const telemetryFieldSchema = z.object({
  variable: z.string(),
  label: z.string(),
  unit: z.string().nullable(),
  // Free-form per spec ("integer" | "float" | "string" | "bool" | "json" today, but not
  // declared as an enum in the OpenAPI schema) — do not z.enum() it.
  valueType: z.string(),
  aggregatable: z.boolean(),
  // Observed on the real API as `null` for fields with no enum/state values —
  // the OpenAPI spec declares this `type: object` with no `nullable`, but the
  // live backend doesn't honour that.
  enumChoices: z.record(z.string(), z.unknown()).nullable(),
  deviceType: z.string(),
});
export type TelemetryField = z.infer<typeof telemetryFieldSchema>;

export const telemetryFieldListSchema = z.array(telemetryFieldSchema);

// ─── Telemetry series ───────────────────────────────────────────────────────────

export const telemetryMetricValueSchema = z.union([z.number(), z.string(), z.boolean(), z.null()]);

/**
 * `time` is the only key the API declares. Every other key is a dynamic metric whose
 * name depends on the requested variable and resolution — see utils/telemetryKeys.ts.
 *
 * `time` is kept as a raw ISO string rather than run through apiDate(): the chart needs
 * epoch milliseconds, and apiDate() would allocate then immediately discard a Date for
 * each of up to ~7,200 points per fetch. The mapper layer parses it once.
 */
export const telemetrySeriesPointSchema = z
  .object({ time: z.string() })
  .catchall(telemetryMetricValueSchema);
export type TelemetrySeriesPoint = z.infer<typeof telemetrySeriesPointSchema>;

export const telemetryDeviceSeriesSchema = z.object({
  deviceId: z.number(),
  deviceName: z.string(),
  deviceType: z.string(),
  series: z.array(telemetrySeriesPointSchema),
});
export type TelemetryDeviceSeries = z.infer<typeof telemetryDeviceSeriesSchema>;

export const telemetryResponseSchema = z.object({
  result: z.array(telemetryDeviceSeriesSchema),
});
export type TelemetryResponse = z.infer<typeof telemetryResponseSchema>;

// Deliberately never 'auto' on outgoing requests — see config.ts.
export const telemetryResolutionSchema = z.enum(['1min', '15min']);
export type TelemetryResolution = z.infer<typeof telemetryResolutionSchema>;

// ─── EMS connectivity status ─────────────────────────────────────────────────────

/**
 * Provisional — no backend endpoint exists yet for EMS connectivity.
 * See services/EmsStatusService.ts for the proposed contract.
 */
export const emsStatusSchema = z.object({
  lastMessageAt: apiDate().nullable(),
  isOnline: z.boolean(),
});
export type EmsStatus = z.infer<typeof emsStatusSchema>;
